import request from '../../../core/wrappers/axios-wrapper';
import { S3CommandUrl } from '../../../types/constants/apiUrls/AIPrompt/S3Command';
import { RequestType } from '../../../types/enums/request-enums';

export interface CreateS3FolderRequest {
    /** Parent prefix (e.g. "DEV/" or "") */
    prefix: string;
    /** Single path segment, no slashes */
    name: string;
}

export interface CreateS3FolderResponse {
    key: string;
}

function mapCreateFolder(raw: Record<string, unknown>): CreateS3FolderResponse {
    return {
        key: (raw.key ?? raw.Key ?? '') as string,
    };
}

export const CreateS3Folder = async (data: CreateS3FolderRequest): Promise<CreateS3FolderResponse> => {
    const raw = (await request({
        url: S3CommandUrl.CreateFolder,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;
    return mapCreateFolder(raw);
};

export interface RenameS3Request {
    sourceKey: string;
    newName: string;
}

export interface RenameS3Response {
    destinationKey: string;
}

export const RenameS3Object = async (data: RenameS3Request): Promise<RenameS3Response> => {
    const raw = (await request({
        url: S3CommandUrl.Rename,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;
    return {
        destinationKey: (raw.destinationKey ?? raw.DestinationKey ?? '') as string,
    };
};

export interface MoveS3Request {
    sourceKey: string;
    destinationPrefix: string;
}

export const MoveS3Object = async (data: MoveS3Request): Promise<RenameS3Response> => {
    const raw = (await request({
        url: S3CommandUrl.Move,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;
    return {
        destinationKey: (raw.destinationKey ?? raw.DestinationKey ?? '') as string,
    };
};

export interface DeleteS3Request {
    key: string;
}

export const DeleteS3Object = async (data: DeleteS3Request): Promise<void> => {
    await request({
        url: S3CommandUrl.Delete,
        method: RequestType.POST,
        data,
    });
};

export const UploadS3File = async (prefix: string, file: File, onProgress?: (progress: number) => void): Promise<void> => {
    const chunkSize = 5 * 1024 * 1024; // 5MB chunks
    const totalChunks = Math.ceil(file.size / chunkSize) || 1;
    let chunkIndex = 0;

    const safePrefix = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
    const key = safePrefix ? `${safePrefix}/${file.name}` : file.name;

    while (chunkIndex < totalChunks) {
        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const base64Data = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                const base64 = result.includes(',') ? result.split(',')[1] : result;
                resolve(base64);
            };
            reader.readAsDataURL(chunk);
        });

        const req = {
            fileName: file.name,
            key: key,
            chunkIndex,
            data: base64Data,
            isLastChunk: chunkIndex === totalChunks - 1,
            totalChunks,
            userName: 'StudioBrowser',
        };

        const response = (await request({
            url: S3CommandUrl.Upload,
            method: RequestType.POST,
            data: req,
        })) as Record<string, unknown>;

        const completed = Boolean(response?.completed ?? response?.Completed);
        const isLast = chunkIndex === totalChunks - 1;
        if (isLast && !completed) {
            throw new Error(
                String(response?.message ?? response?.Message ?? 'S3 upload did not complete.'),
            );
        }

        chunkIndex++;
        if (onProgress) onProgress(Math.round((chunkIndex / totalChunks) * 100));
    }
};
